<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  VFormFilterButtonWithForm,
  VFormFilterButtonWithFormProps,
} from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IPostbackCondition } from '@modules/postbacks/submodules/edit/types';
import { filterOutAttributeSchema } from '@modules/affiliate-program-commissions/submodules/edit/utils';
import VFormButtonsWithFormGroupWidget from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/VFormButtonsWithFormGroupWidget.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_POSTBACKS } from '@modules/postbacks/constants';
import { isNumber } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  PostbackConditionsEmits,
  PostbackConditionsProps,
} from './types';

const props = withDefaults(defineProps<PostbackConditionsProps>(), {});
const emit = defineEmits<PostbackConditionsEmits>();

const { getTranslation } = useI18n();

const tempDeals = ref<IPostbackCondition[]>([]);
const tempAddDeal = ref<IPostbackCondition>();

watchImmediate(() => props.items, (newValue) => {
  tempDeals.value = [...newValue];
  tempAddDeal.value = undefined;
}, { deep: true });

function getObtainedResults(excludeIndex: number): string[] {
  return props.items.map((item, index) => (
    index !== excludeIndex ? item.obtained_result : ''
  ));
}

const dealsFormSchema = computed<FormSchema<IPostbackCondition>>(() => ({
  obtained_result: {
    type: EFormInputTypes.SELECT,
    options: props.obtainedResultOptions,
    label: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_FORM_CONDITION_RESULT').value,
    required: true,
  },
  condition_for_comparison: {
    type: EFormInputTypes.SELECT,
    options: [],
    label: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_FORM_CONDITION_CONDITION').value,
    required: true,
  },
  desired_value: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_FORM_CONDITION_VALUE').value,
    required: true,
  },
}));

const buttons = computed<VFormFilterButtonWithForm<IPostbackCondition>[]>(() => (
  props.items.map((item, index) => {
    const formSchema = {
      ...dealsFormSchema.value,
      obtained_result: {
        ...dealsFormSchema.value.obtained_result,
        options: props.obtainedResultOptions.filter((option) => !getObtainedResults(index).includes(option.value)),
      },
      condition_for_comparison: {
        ...dealsFormSchema.value.condition_for_comparison,
        options: (
          props.comparisonOptions[item.obtained_result] || []
        ).map((comparisonItem) => ({
          value: comparisonItem,
          label: comparisonItem,
        })),
      },
    };

    return {
      formSchema: filterOutAttributeSchema(formSchema, index, tempDeals.value),
      value: item,
      type: 'secondary',
      allowRemove: index > 0,
    } as VFormFilterButtonWithForm<IPostbackCondition>;
  })
));

const addButton = computed<VFormFilterButtonWithFormProps<IPostbackCondition> | undefined>(() => {
  const options = props.obtainedResultOptions.filter((option) => !getObtainedResults(-1).includes(option.value));

  if (!options.length) {
    return undefined;
  }

  const formSchema = {
    ...dealsFormSchema.value,
    obtained_result: {
      ...dealsFormSchema.value.obtained_result,
      options,
    },
    condition_for_comparison: {
      ...dealsFormSchema.value.condition_for_comparison,
      options: (
        props.comparisonOptions[tempAddDeal.value?.obtained_result || ''] || []
      ).map((comparisonItem) => ({
        value: comparisonItem,
        label: comparisonItem,
      })),
    },
  };
  if (tempAddDeal.value?.condition_for_comparison === 'any' && formSchema.desired_value) {
    delete formSchema.desired_value;
  }

  return {
    label: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CONDITION_ADD').value,
    formSchema,
  };
});

function onChange(index: number, value: IPostbackCondition): void {
  const button = props.items[index];

  if (button) {
    emit('change', {
      ...button,
      ...value,
    });
  }
}

function onInput(index: number | string, value: IPostbackCondition): void {
  if (isNumber(index)) {
    tempDeals.value[index] = value;
  }

  if (index === 'add') {
    tempAddDeal.value = value;
  }
}

function onRemove(index: number): void {
  const button = props.items[index];

  if (button) {
    emit('remove', button);
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'PostbackConditions'">
    <div class="mb-2 block text-muted-color">
      {{ $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CONDITION_LABEL') }}
    </div>
    <VFormButtonsWithFormGroupWidget
      :buttons="buttons"
      :add-button="addButton"
      :form-title="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CONDITION_MODAL_TITLE')"
      :form-submit-btn="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CONDITION_MODAL_SAVE')"
      :form-close-btn="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CONDITION_MODAL_CANCEL')"
      :readonly="readonly"
      @change="onChange"
      @remove="onRemove"
      @add="emit('add', $event)"
      @input="onInput"
    />
  </div>
</template>
